// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { useNavigate } from "@tanstack/react-router";
import type { PaginationState, SortingState } from "@tanstack/react-table";
import { Filter, IdCard, X } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "@/api/client";
import { useVendorsQuery, type VendorRow } from "@/api/hooks/use-vendors";
import { DataTable, type DataTableColumnDef } from "@/components/data-table";
import { PageHeader } from "@/components/layout/page-header";
import { ChainLink } from "@/components/shared/chain-link";
import { CopyableKey } from "@/components/shared/copyable-key";
import { DateValue } from "@/components/shared/datetime-value";
import { FilterPresets } from "@/components/shared/filter-presets";
import { FilterSelect } from "@/components/shared/filter-select";
import { FilterToggle } from "@/components/shared/filter-toggle";
import { KeyChip } from "@/components/shared/key-chip";
import { RowId } from "@/components/shared/row-id";
import { StatusIndicator } from "@/components/shared/status-indicator";
import { VendorLink } from "@/components/shared/vendor-link";
import { Button } from "@/components/ui/button";
import { CreateVendorDialog } from "@/features/iam/create-vendor-dialog";
import { ToggleVendorActiveDialog } from "@/features/iam/toggle-vendor-active-dialog";
import { VendorActionsMenu } from "@/features/iam/vendor-actions-menu";
import { useBackofficeContext } from "@/hooks/use-backoffice-context";
import { useEscapeToClearFilters } from "@/hooks/use-escape-to-clear-filters";
import { useAuth } from "@/lib/auth/auth-provider";
import { PAGE_PERMISSIONS } from "@/lib/auth/page-permissions";
import { downloadCsv } from "@/lib/csv";
import { decodeFilters, encodeFilters } from "@/lib/filter-params";
import { getDefaultPageSize } from "@/lib/preferences";
import { Route } from "@/routes/_authenticated/iam/vendors/index";

const FILTER_DEFAULTS = {
  search: "",
  onlyActive: true,
  chain: "",
  page: 0,
  get pageSize() {
    return getDefaultPageSize();
  },
  sortId: "vendorId",
  sortDesc: false,
};

type VendorsSortBy = "vendorId" | "name" | "chainName" | "active";

export function VendorsList() {
  const { t } = useTranslation("back-office");
  const { hasPermission } = useAuth();
  const canEdit = hasPermission(PAGE_PERMISSIONS.iamVendorsEdit);
  const navigate = useNavigate();
  const { f } = Route.useSearch();
  const initial = useMemo(() => decodeFilters(f, FILTER_DEFAULTS), [f]);

  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: initial.page,
    pageSize: initial.pageSize,
  });
  const [sorting, setSorting] = useState<SortingState>([{ id: initial.sortId, desc: initial.sortDesc }]);
  const [search, setSearch] = useState(initial.search);
  const [onlyActive, setOnlyActive] = useState(initial.onlyActive);
  const [chainFilter, setChainFilter] = useState(initial.chain);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [toggleVendor, setToggleVendor] = useState<VendorRow | null>(null);

  const { data: context, isLoading: contextLoading } = useBackofficeContext();

  // The single description of "what is filtered right now" — shared by the URL sync and the presets.
  const currentFilters = useMemo(
    () => ({
      search,
      onlyActive,
      chain: chainFilter,
      page: pagination.pageIndex,
      pageSize: pagination.pageSize,
      sortId: sorting[0]?.id ?? "vendorId",
      sortDesc: sorting[0]?.desc ?? false,
    }),
    [search, onlyActive, chainFilter, pagination.pageIndex, pagination.pageSize, sorting],
  );

  const syncUrl = useCallback(
    (overrides: Partial<typeof FILTER_DEFAULTS> = {}) => {
      const encoded = encodeFilters({ ...currentFilters, ...overrides }, FILTER_DEFAULTS);
      navigate({
        to: "/iam/vendors",
        search: encoded ? { f: encoded } : {},
        replace: true,
      });
    },
    [currentFilters, navigate],
  );

  const applyPreset = useCallback(function applyPreset(next: typeof FILTER_DEFAULTS) {
    setSearch(next.search);
    setOnlyActive(next.onlyActive);
    setChainFilter(next.chain);
    setSorting([{ id: next.sortId, desc: next.sortDesc }]);
    // A preset carries no page index, and page size stays the viewer's own preference.
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, []);

  useEffect(
    function synchronizeFiltersToUrl() {
      syncUrl();
    },
    [syncUrl],
  );

  const sortBy = (sorting[0]?.id ?? "vendorId") as VendorsSortBy;
  const orderBy = sorting[0]?.desc ? ("desc" as const) : ("asc" as const);

  const isFiltered = !!(search || !onlyActive || chainFilter);

  const clearFilters = useCallback(function clearFilters() {
    setSearch("");
    setOnlyActive(true);
    setChainFilter("");
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, []);

  useEscapeToClearFilters({ isFiltered, clearFilters, disabled: toggleVendor !== null });

  const { data, isLoading, refetch, isFetching } = useVendorsQuery({
    page: pagination.pageIndex + 1,
    limit: pagination.pageSize,
    search: search || undefined,
    sortBy,
    orderBy,
    onlyActive: onlyActive || undefined,
    chainName: chainFilter || undefined,
  });

  const handleExport = useCallback(async () => {
    const res = await api.GET("/v1/vendors", {
      params: {
        query: {
          page: 1,
          limit: -1,
          search: search || undefined,
          sortBy,
          orderBy,
          "filter[onlyActive]": onlyActive || undefined,
          "filter[chainName]": chainFilter || undefined,
        },
      },
    });
    const result = res.data as unknown as { vendors: VendorRow[] };
    downloadCsv(
      "vendors.csv",
      ["ID", "Name", "API Key", "Chain", "Created", "Status"],
      result.vendors.map((v) => [
        String(v.vendorId),
        v.name ?? "",
        v.apiKey ?? "",
        v.chainName ?? "",
        v.createdAt ?? "",
        v.isActive ? "Active" : "Inactive",
      ]),
    );
  }, [search, sortBy, orderBy, onlyActive, chainFilter]);

  const columns = useMemo<DataTableColumnDef<VendorRow>[]>(
    () => [
      {
        accessorKey: "vendorId",
        header: t("vendors.list.columns.id"),
        size: 80,
        enableSorting: true,
        enableHiding: false,
        meta: { columnLabel: t("vendors.list.columns.id"), className: "pl-4", forceWidth: true, sticky: "left" },
        cell: ({ row }) => <RowId id={row.original.vendorId} />,
      },
      {
        accessorKey: "name",
        header: t("vendors.list.columns.name"),
        enableSorting: true,
        meta: { columnLabel: t("vendors.list.columns.name") },
        cell: ({ row, table }) => {
          // Surface the API key as a sub-line when its dedicated column is hidden from the view.
          const apiKeyColumnVisible = table.getColumn("apiKey")?.getIsVisible() ?? false;
          return (
            <div className="flex flex-col">
              {row.original.name ? (
                <VendorLink
                  vendorName={row.original.name}
                  legibleName={row.original.name}
                  showIcon={false}
                  className="font-bold"
                />
              ) : (
                <span className="text-muted-foreground">—</span>
              )}
              {row.original.apiKey && !apiKeyColumnVisible && <CopyableKey value={row.original.apiKey} />}
            </div>
          );
        },
      },
      {
        accessorKey: "apiKey",
        header: t("vendors.list.columns.apiKey"),
        enableSorting: false,
        meta: { columnLabel: t("vendors.list.columns.apiKey"), defaultHidden: true },
        cell: ({ row }) =>
          row.original.apiKey ? (
            <KeyChip label={row.original.apiKey} />
          ) : (
            <span className="text-muted-foreground">—</span>
          ),
      },
      {
        accessorKey: "chainName",
        header: t("vendors.list.columns.chain"),
        enableSorting: true,
        meta: { columnLabel: t("vendors.list.columns.chain") },
        cell: ({ row }) =>
          row.original.chainName ? (
            <ChainLink chainName={row.original.chainName} legibleName={row.original.chainName} />
          ) : (
            <span className="text-muted-foreground">—</span>
          ),
      },
      {
        accessorKey: "createdAt",
        header: t("vendors.list.columns.createdAt"),
        enableSorting: false,
        meta: { columnLabel: t("vendors.list.columns.createdAt") },
        cell: ({ row }) => <DateValue value={row.original.createdAt} />,
      },
      {
        accessorKey: "isActive",
        id: "active",
        header: t("vendors.list.columns.status"),
        size: 80,
        enableSorting: true,
        meta: { columnLabel: t("vendors.list.columns.status") },
        cell: ({ row }) => <StatusIndicator active={row.original.isActive} />,
      },
      {
        id: "actions",
        size: 50,
        meta: { forceWidth: true, sticky: "right" },
        enableSorting: false,
        enableHiding: false,
        cell: ({ row }) => (
          <VendorActionsMenu isActive={row.original.isActive} onToggleActive={() => setToggleVendor(row.original)} />
        ),
      },
    ],
    [t],
  );

  return (
    <div>
      <PageHeader title={t("vendors.title")} />
      <div className="p-6">
        <DataTable
          storageKey="vendors-list"
          columns={columns}
          data={data?.vendors ?? []}
          totalCount={data?.count ?? 0}
          isLoading={isLoading}
          pagination={pagination}
          onPaginationChange={setPagination}
          sorting={sorting}
          onSortingChange={setSorting}
          search={search}
          onSearchChange={setSearch}
          searchDebounceMs={500}
          onRefresh={refetch}
          isRefreshing={isFetching}
          onExport={handleExport}
          searchPlaceholder={t("vendors.list.searchPlaceholder")}
          presets={
            <FilterPresets
              storageKey="vendors-list"
              defaults={FILTER_DEFAULTS}
              current={currentFilters}
              onApply={applyPreset}
            />
          }
          filters={
            <>
              <FilterSelect
                label={t("vendors.list.filter.chain")}
                value={chainFilter}
                onChange={(v) => {
                  setChainFilter(v);
                  setPagination((prev) => ({ ...prev, pageIndex: 0 }));
                }}
                options={(context?.chains ?? []).map((c) => ({ label: c.display, value: c.value }))}
                searchPlaceholder={t("vendors.list.filter.searchChains")}
                emptyText={t("vendors.list.filter.noChains")}
                loading={contextLoading}
              />
              <FilterToggle
                active={onlyActive}
                onChange={() => {
                  setOnlyActive((prev) => !prev);
                  setPagination((prev) => ({ ...prev, pageIndex: 0 }));
                }}
                icon={Filter}
                label={t("common.active")}
              />
              {isFiltered && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-9 w-9 text-muted-foreground hover:text-foreground"
                  onClick={clearFilters}
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </>
          }
          toolbar={
            canEdit ? (
              <Button onClick={() => setCreateDialogOpen(true)}>
                <IdCard className="h-4 w-4" />
                {t("vendors.list.addVendorButton")}
              </Button>
            ) : null
          }
        />
      </div>

      <CreateVendorDialog open={createDialogOpen} onOpenChange={setCreateDialogOpen} />
      <ToggleVendorActiveDialog vendor={toggleVendor} onClose={() => setToggleVendor(null)} />
    </div>
  );
}
